/**
 * The study API: findings, themes, threads, packs, and the two inbound doors.
 *
 * Split from `account-client.js` along the seam the product already draws
 * everywhere else — *the root site owns the account; corpus sites own the
 * study*. Only a corpus site loads this file, which is why the root image can go
 * on copying `account-client.js` alone.
 *
 * The transport comes from there rather than being written again here: same
 * headers, same `AccountError`, same base path. Two copies of a fetch wrapper is
 * how two halves of one contract start disagreeing about what a failure is.
 *
 * `tests/test_account_contract.py` reads both files and asserts every path they
 * build is a route the server actually serves.
 */

import { AccountError, call, post, put, query, remove } from './account-client.js'
import { responseSentence } from './user-errors.js'

// --- findings ---------------------------------------------------------------

/**
 * A page of this account's findings, newest first, with the total before paging.
 *
 * Filtering and paging are the server's job because a corpus grows without
 * limit: a list page that ships every row and hides most of them gets slower
 * every time the reader does good work.
 */
export const listFindings = (parameters) => call(`/findings${query(parameters)}`)

export const readFinding = (id) =>
  call(`/findings/${id}`).then((result) => result.finding)

/** Write one finding by hand — a claim the reader states themselves. */
export const createFinding = (fields) =>
  post('/findings', fields).then((result) => result.finding)

export const updateFinding = (id, fields) =>
  put(`/findings/${id}`, fields).then((result) => result.finding)

export const deleteFinding = (id) => remove(`/findings/${id}`).then(() => undefined)

/**
 * Withdraw a claim without deleting it — the framework's answer to "I no longer
 * hold this", and what the delete refusal offers instead.
 */
export const retireFinding = (id) =>
  post(`/findings/${id}/retire`).then((result) => result.finding)

/**
 * Adopt an imported finding — "I believe this to be true" — as your own copy.
 *
 * Resolves to the **new** finding, not the original: the reader's next act is
 * to work their copy, and handing back the import would hand back the one they
 * still cannot edit. Everything is copied verbatim except the status, which the
 * reader chooses and which may never exceed the original's.
 */
export const adoptFinding = (id, status) =>
  post(`/findings/${id}/adopt`, { status }).then((result) => result.finding)

/** Re-check this finding's citations. A report, never an edit. */
export const verifyFinding = (id) =>
  post(`/findings/${id}/verify`).then((result) => result.finding)

/** "I know, it is fine." An accepted check never returns to the queue. */
export const acceptCheck = (findingId, checkId) =>
  post(`/findings/${findingId}/checks/${checkId}/accept`).then(
    (result) => result.finding,
  )

/**
 * "I have seen what changed upstream." Restamps this copy's source fingerprint.
 *
 * Never edits the claim. The reader adopted a reading and its author has since
 * revised it; acknowledging records that they have looked, and nothing more.
 */
export const markSourceSeen = (id) => post(`/findings/${id}/source-seen`)

/**
 * Keep an answer's claims as findings — one finding per claim, so the result
 * is a list. A claim is the atomic thing a corpus accumulates; the answer
 * around it is a conversation, and conversations do not accumulate.
 */
export const saveFinding = (finding) =>
  post('/findings/from-answer', finding).then((result) => result.findings)

/** The review queue: citation problems, dangling references, and moved sources. */
export const reviewQueue = (corpus) => call(`/review${query({ corpus })}`)

// --- themes, threads --------------------------------------------------------

export const listThemes = (corpus) =>
  call(`/themes${query({ corpus })}`).then((result) => result.themes)

export const createTheme = (fields) =>
  post('/themes', fields).then((result) => result.theme)

export const readTheme = (id) => call(`/themes/${id}`).then((result) => result.theme)

export const updateTheme = (id, fields) =>
  put(`/themes/${id}`, fields).then((result) => result.theme)

export const deleteTheme = (id) => remove(`/themes/${id}`).then(() => undefined)

export const listThreads = (corpus) =>
  call(`/threads${query({ corpus })}`).then((result) => result.threads)

export const createThread = (fields) =>
  post('/threads', fields).then((result) => result.thread)

export const readThread = (id) => call(`/threads/${id}`).then((result) => result.thread)

export const updateThread = (id, fields) =>
  put(`/threads/${id}`, fields).then((result) => result.thread)

export const deleteThread = (id) => remove(`/threads/${id}`).then(() => undefined)

// --- packs ------------------------------------------------------------------

export const listPacks = (corpus) => call(`/packs${query({ corpus })}`)

export const createPack = (fields) =>
  post('/packs', fields).then((result) => result.pack)

export const readPack = (id) => call(`/packs/${id}`).then((result) => result.pack)

export const updatePack = (id, fields) =>
  put(`/packs/${id}`, fields).then((result) => result.pack)

export const deletePack = (id) => remove(`/packs/${id}`).then(() => undefined)

/**
 * Render this pack once and keep the bytes — the act that makes it public.
 *
 * Returns the feed address alongside the version, because the reader's next
 * question is always "where do people point their subscription?".
 */
export const publishPack = (id, version) =>
  post(`/packs/${id}/publish`, { version: version ?? null })

export const packVersions = (id) =>
  call(`/packs/${id}/versions`).then((result) => result.versions)

/**
 * Where a published version can be downloaded from.
 *
 * Built here rather than sent by the server because it is the public feed's own
 * address, which `feed_routes` already serves and which anybody — a subscriber,
 * the framework's `packs.py`, a stranger with the link — can fetch without a
 * session. Not an `/api/account/` path, so it is a plain string, not a call.
 */
export const packArchiveUrl = (authorSlug, packSlug, version) =>
  `/packs/${encodeURIComponent(authorSlug)}/${encodeURIComponent(
    `${packSlug}-v${version}.zip`,
  )}`

export const feedUrl = (authorSlug) =>
  `/packs/${encodeURIComponent(authorSlug)}/feed.json`

// --- imports: a snapshot ----------------------------------------------------

/** Whose findings this account holds under quarantine, and how many. */
export const listImports = (corpus) =>
  call(`/imports${query({ corpus })}`).then((result) => result.imports)

/** Put a whole borrowed corpus back down — one act, as the framework has it. */
export const deleteImport = (author) =>
  remove(`/imports/${encodeURIComponent(author)}`)

/**
 * Say that an import's author is you: its findings become your own, in place,
 * each keeping the origin it was written with. Resolves to how many moved.
 */
export const claimImport = (author) =>
  post(`/imports/${encodeURIComponent(author)}/claim`).then((result) => result.findings)

/**
 * Upload a pack zip, reporting how far it has got.
 *
 * An `XMLHttpRequest` rather than `fetch`, for one reason: `fetch` cannot
 * report upload progress, and a pack is the one body here big enough for a
 * reader to wonder whether anything is happening. The work has two phases a
 * reader would call different things — the bytes travelling, then the server
 * checking and storing every finding (a whole corpus takes a while) — so
 * `onProgress` hears `{ phase: 'sending', fraction }` while the body goes out
 * and `{ phase: 'importing' }` once it has all arrived.
 *
 * `FormData` sets its own `content-type`, boundary included, which is why the
 * JSON `call` helper cannot carry this. The failure shape is kept identical:
 * the same `AccountError`, with the same sentence `call` would have chosen.
 */
export function uploadPack(file, corpus, consent, onProgress = () => {}) {
  const body = new FormData()
  body.append('upload', file)
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', `/api/account/imports/upload${query({ corpus, consent })}`)
    request.responseType = 'json'
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress({ phase: 'sending', fraction: event.loaded / event.total })
    })
    request.upload.addEventListener('load', () => onProgress({ phase: 'importing' }))
    request.addEventListener('load', () => {
      // `response` is null when the body was not JSON — a proxy's error page.
      const answer = request.response ?? {}
      if (request.status >= 200 && request.status < 300) {
        resolve(answer)
        return
      }
      reject(new AccountError(responseSentence(answer.detail, request.status), request.status, answer.kind))
    })
    // No response at all: the connection, not the server. `describeFailure`
    // turns a plain Error into the "could not be reached" sentence.
    request.addEventListener('error', () => reject(new Error('pack upload: network error')))
    request.send(body)
    // Said at once rather than at the first progress event, which a small file
    // on a fast line may never produce: the bar appears the moment the press
    // does something, and the 'load' listener above still moves it to importing.
    onProgress({ phase: 'sending', fraction: 0 })
  })
}

// --- subscriptions: a live connection ---------------------------------------

/** Every feed this reader follows, and whether any publisher has moved on. */
export const listSubscriptions = () =>
  call('/subscriptions').then((result) => result.subscriptions)

/**
 * Follow one pack on a sourcetext.ai feed.
 *
 * The URL must name this site. A feed elsewhere would mean the server fetching
 * an address a reader typed, and the answer to that is not a careful fetch but
 * no fetch: download that pack and upload the file instead.
 */
export const subscribeToFeed = (url, pack, corpus, consent) =>
  post('/subscriptions', { url, pack, corpus, consent }).then(
    (result) => result.subscription,
  )

/** Stop following. Everything already received stays — that is a different button. */
export const unsubscribeFromFeed = (id) =>
  remove(`/subscriptions/${id}`).then(() => undefined)
